// Mock tests: a clear place to start, then the full shelf of sittings as a tidy
// catalog rather than a wall of identical cards.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, Monitor } from 'lucide-react';

import { api } from '@/lib/api';
import { mockLabel } from '@/lib/labels';
import AppShell from '@/components/AppShell';

const SECTION_ORDER = {
  celpip: ['listening', 'reading', 'writing', 'speaking'],
  pte: ['speaking', 'writing', 'reading', 'listening'],
};

const EXAM_NOTE = {
  celpip: 'Sections run Listening through Speaking with no review until the end, like the real sitting.',
  pte: 'Parts run Speaking & Writing, then Reading, then Listening, the real PTE order, forward-only.',
};

const EXAM_TIME = { celpip: '3 hours', pte: '2 hours' };

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function MocksPage() {
  const { exam } = useParams();
  const [forms, setForms] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.mockIndex(exam)
      .then((res) => setForms([...(res.data.forms || [])].sort((a, b) => a.form - b.form)))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load mock tests.'));
  }, [exam]);

  if (error) return <AppShell><p className="text-red-600">{error}</p></AppShell>;

  const order = SECTION_ORDER[exam] || SECTION_ORDER.celpip;
  const time = EXAM_TIME[exam] || '~2 hours';
  const scored = (f) => order.reduce((a, s) => a + (f.sections[s]?.questions || 0), 0);
  const sectionsOf = (f) => order.filter((s) => f.sections[s]);

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mono-stat">{exam} · full sittings</p>
          <h1 className="h-display mt-2 text-3xl">Mock tests.</h1>
        </div>
        <Link to={`/${exam}/exam-walkthrough`} data-testid="exam-walkthrough-btn"
          className="inline-flex shrink-0 items-center gap-2 border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900">
          <Monitor className="h-4 w-4" /> Walk through the exam
        </Link>
      </div>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-500">{EXAM_NOTE[exam]}</p>

      {!forms ? (
        <p className="mt-10 text-slate-400">Loading…</p>
      ) : forms.length === 0 ? (
        <p className="mt-10 text-sm text-slate-500">Mock forms arrive here as content is packaged.</p>
      ) : (
        <>
          {/* Recommended start, a clear place to begin, not 30 equal buttons */}
          {(() => {
            const f = forms[0];
            return (
              <div className="mt-8 flex flex-col gap-6 border border-slate-900 bg-slate-900 p-7 text-white sm:flex-row sm:items-center sm:justify-between" data-testid="mock-featured">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Recommended start</p>
                  <h2 className="h-display mt-1 text-2xl">Mock {mockLabel(f.form)}</h2>
                  <p className="mt-2 max-w-md text-sm leading-6 text-slate-300">
                    A full timed sitting: {sectionsOf(f).map(cap).join(', ')}. {scored(f)} scored questions,
                    forward-only like the real test.
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <Clock className="h-3.5 w-3.5" /> about {time}
                  </p>
                </div>
                <Link
                  to={`/${exam}/mock/${f.form}`}
                  data-testid="mock-featured-start"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
                >
                  Begin Mock {mockLabel(f.form)} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            );
          })()}

          {/* The full shelf, a compact, scannable catalog */}
          <div className="mt-12 flex items-baseline justify-between border-b-2 border-slate-900 pb-2">
            <h2 className="h-display text-xl">All sittings</h2>
            <span className="text-xs text-slate-400">{forms.length} mocks · {time} each</span>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {forms.map((f) => (
              <Link
                key={f.form}
                to={`/${exam}/mock/${f.form}`}
                data-testid={`mock-card-${f.form}`}
                className="group flex items-center gap-4 border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-slate-900"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-bold tabular-nums text-slate-500 transition-colors group-hover:bg-slate-900 group-hover:text-white">
                  {String(mockLabel(f.form)).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[15px] font-semibold text-slate-900">Mock {mockLabel(f.form)}</p>
                  <p className="text-xs text-slate-400">{scored(f)} scored questions · {sectionsOf(f).length} sections</p>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-900" />
              </Link>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
